import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type DragEvent,
} from "react";
import type {
  ComposeStatus,
  EditableCut,
  EditResponse,
  EditSettings,
  Motion,
} from "@studio/shared";
import { CUT_LOOP_IMAGE_SECONDS, planCutLoop } from "@studio/shared";
import { ErrorPage, InlineError, LoadingPage, StudioShell, mvStudioStep } from "../components/StudioShell";
import { TitleEditor } from "../components/TitleEditor";
import {
  ApiError,
  apiRequest,
  getComposeStatus,
  getEdit,
  saveCutMotion,
  saveCutOrder,
  saveEditSettings,
  saveWorkTitle,
  startCompose,
  startVideoJob,
  stopCompose,
  useJobProgress,
  useWork,
} from "../lib/api";
import { navigate } from "../lib/navigation";
import { pathForStep } from "../lib/session";
import { videoFailureMessage } from "../lib/videoFailure";

/**
 * 고를 수 있는 전환 효과.
 *
 * 🚨 **crossfade(부드럽게 겹치기)는 뺐다** — 그 효과만 이어붙이기에서 영상을 **다시 인코딩**한다.
 *    실측(2026-08-08 · starter): 이어붙이기 구간이 `0.2s → 57.9s`, 합성 전체가 58초(38%) 더 걸렸다.
 *    25명 학급이면 61분 vs 38분이고, 1차시가 45분이다. 시간이 결과물 인상보다 앞선다는
 *    jery 판단(2026-08-08).
 *
 *    📌 그 뒤 합성 전체가 빨라졌지만(80.2s · 25명 33.4분) **이 판단은 그대로다** — crossfade 만
 *    재인코딩한다는 성질이 바뀐 게 아니라서, 되살리면 그만큼이 다시 붙는다.
 *
 * 🔑 서버·DB 는 crossfade 를 **여전히 허용**한다. 이미 그 값으로 저장된 작품이 있고,
 *    값을 부정하면 그 행들의 갱신이 막힌다. 「새로 못 고른다」와 「저장된 값을 부정한다」는
 *    다른 일이다.
 */
const transitions: Array<{ value: EditSettings["transition"]; label: string }> = [
  { value: "fadeblack", label: "어두워졌다 밝게" },
  { value: "hardcut", label: "바로 넘기기" },
];

const motions: Array<{ value: Motion; label: string; caption: string }> = [
  { value: "zoom_in", label: "줌인", caption: "무료 · 바로 됨" },
  { value: "zoom_out", label: "줌아웃", caption: "무료 · 바로 됨" },
  { value: "pan", label: "팬", caption: "무료 · 바로 됨" },
  { value: "ai_video", label: "AI 영상", caption: "5초 영상" },
];

const activeStates = new Set(["queued", "dispatched", "cancel_requested"]);

function editError(error: unknown): string {
  if (!(error instanceof ApiError)) return "요청을 처리하지 못했어요. 잠시 뒤 다시 해 주세요.";
  switch (error.code) {
    case "LIMIT_EXCEEDED":
      return "남은 AI 영상 횟수가 없어요. 선생님께 기회를 요청해 주세요.";
    case "JOB_ACTIVE":
      return "이미 만드는 작업이 있어요. 현재 작업을 먼저 확인해 주세요.";
    case "IDEMPOTENCY_CONFLICT":
      return "이전 요청과 겹쳤어요. 새 요청으로 다시 시도해 주세요.";
    case "IDEMPOTENCY_KEY_REQUIRED":
      return "요청 키를 만들지 못했어요. 다시 시도해 주세요.";
    case "CUTS_INCOMPLETE":
      return "모든 컷 그림을 먼저 완성해 주세요.";
    case "CLIP_MISSING":
      return "AI 영상으로 고른 컷을 먼저 만들어 주세요.";
    case "FEATURE_DISABLED":
      return "AI 영상 기능을 준비하고 있어요.";
    case "STEP_GUARD":
      return "편집 상태가 바뀌었어요. 컷을 다시 확인해 주세요.";
    default:
      return error.message;
  }
}

function formatTime(value: number): string {
  const seconds = Math.max(0, Math.floor(value));
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
}

export function EditPage({ workId }: { workId: string }) {
  const work = useWork(workId);
  const [edit, setEdit] = useState<EditResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const refresh = useCallback(async (): Promise<EditResponse | null> => {
    try {
      const next = await getEdit(workId);
      setEdit(next);
      setLoadError(null);
      return next;
    } catch (error) {
      setLoadError(editError(error));
      return null;
    } finally {
      setLoading(false);
    }
  }, [workId]);

  useEffect(() => {
    setLoading(true);
    void refresh();
  }, [refresh]);

  if (loading || work.loading) return <LoadingPage label="편집실을 불러오는 중..." />;
  if (!edit || !work.data) {
    return <ErrorPage message={loadError ?? work.error?.message ?? "편집실을 불러오지 못했어요."} />;
  }
  return (
    <EditWorkspace
      workId={workId}
      edit={edit}
      setEdit={setEdit}
      musicAssetId={work.data.work.music_source?.asset_id ?? null}
      musicSeconds={work.data.work.music_source?.duration_seconds ?? null}
      imageRegenRemaining={work.data.image_regen_remaining}
      refresh={refresh}
    />
  );
}

function EditWorkspace({
  workId,
  edit,
  setEdit,
  musicAssetId,
  musicSeconds,
  imageRegenRemaining,
  refresh,
}: {
  workId: string;
  edit: EditResponse;
  setEdit: React.Dispatch<React.SetStateAction<EditResponse | null>>;
  musicAssetId: string | null;
  musicSeconds: number | null;
  imageRegenRemaining: number;
  refresh: () => Promise<EditResponse | null>;
}) {
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewTime, setPreviewTime] = useState(0);
  const [draggedCutId, setDraggedCutId] = useState<string | null>(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [pageError, setPageError] = useState<string | null>(null);
  const [videoErrors, setVideoErrors] = useState<Record<string, string>>({});
  const [toast, setToast] = useState<{ message: string; retry?: () => void } | null>(null);
  const [compose, setCompose] = useState<ComposeStatus>(edit.compose);
  // 「완성하기」 요청이 가는 동안 · 막 시작한 직후 잠깐 · 중지 요청이 가는 동안 — 버튼을 잠근다.
  // 🩸 2026-10-21 언동중: 「완성하기」 를 누르면 그 위에 진행 카드가 끼어들어 버튼이 밀린다. 두 번째 탭이
  //    카드의 「중지」 에 떨어져 시작 1초 만에 취소된 합성이 6건이었다.
  const [composeStarting, setComposeStarting] = useState(false);
  const [stopLocked, setStopLocked] = useState(false);
  // 중지 요청이 가는 동안은 새 「완성하기」 도 막는다 — 늦은 중지와 새 합성이 서버에서 엇갈리는 틈을 줄인다.
  const [stopping, setStopping] = useState(false);
  // 이 화면에서 「중지」 를 누른 합성 — 서버 일꾼이 실제로 멈추기까지 시간이 걸린다. 그동안 카드가
  // «만드는 중» 으로 남아 막대가 오르면 학생은 중지가 안 된 줄 알고 또 누른다(2026-10-21 브라우저 QA · 약 25초).
  //    같은 job_id 인 동안은 폴링이 dispatched 를 돌려줘도 «멈추는 중» 을 유지한다.
  const [stoppedJobId, setStoppedJobId] = useState<string | null>(null);
  // 「완성하기」 가 앞 조건에 막혀 띄운 안내 — 조건이 풀리면 지운다. 다른 오류(pageError)와 따로 둔다.
  const [composeBlock, setComposeBlock] = useState<"needs_image" | "needs_clip" | null>(null);
  // 컷 AI 영상 POST 가 가는 중인 컷 — 응답 전 연타로 409 가 쌓였다(18초 53회).
  const [videoStarting, setVideoStarting] = useState<Set<string>>(() => new Set());
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const settingsTimer = useRef<number | null>(null);
  // 아직 서버에 안 간 편집 설정과, 가는 중인 저장 — 「완성하기」 가 이것을 기다려야 결과가 superseded 로 버려지지 않는다.
  const pendingSettings = useRef<EditSettings | null>(null);
  const settingsSave = useRef<Promise<void> | null>(null);
  const stopUnlockTimer = useRef<number | null>(null);
  // 같은 틱에 두 번 눌리면 state 는 아직 안 바뀌어 있다 — 막는 판정은 ref 로 한다.
  const composeStartingRef = useRef(false);
  const videoStartingRef = useRef(new Set<string>());
  const videoKeys = useRef(new Map<string, string>());
  const composeKey = useRef<string | null>(null);
  const finalizing = useRef(false);
  /**
   * 「지금 눈앞에서 완성됐다」와 「예전에 완성해 둔 작품이다」를 가르는 표식.
   *
   * 🚨 이것이 없어서 완성작 화면의 «편집실로 돌아가기» 가 동작하지 않았다(2026-08-13 jery 실사용).
   *    편집실은 `compose.state === "succeeded"` 를 보면 곧장 완성작 화면으로 넘긴다. 그런데 그 상태는
   *    **완성된 뒤로는 계속 succeeded** 라, 편집실에 들어오는 순간 조건이 참이 되어 그대로 튕겨 나간다.
   *    = 한 번 완성한 작품은 다시는 편집실에 못 들어간다.
   *
   * 🔑 그래서 «상태값» 이 아니라 «전이» 를 본다. 이 화면에 머무는 동안 만들기가 실제로 돌아간 적이
   *    있을 때만 자동 이동한다. 예전 완성작으로 들어온 사람은 편집실에 남고, 완성작으로는
   *    footer 의 버튼으로 «자기 발로» 간다.
   */
  const composeWatched = useRef(false);

  // 영상 길이 = 지금 붙어 있는 노래의 길이(완성 영상과 같은 기준 · compose-worker).
  //   컷 길이 합은 컷을 나눌 때의 노래 길이라, 노래를 바꾼 뒤에는 틀린다(2026-10-27 Codex 검수 8번).
  const totalDuration = useMemo(
    () => musicSeconds && musicSeconds > 0
      ? musicSeconds
      : edit.cuts.reduce((sum, cut) => sum + cut.duration_seconds, 0),
    [edit.cuts, musicSeconds],
  );
  // AI 영상 컷의 클립 길이 — DB 에 없어서 아래 컷 카드의 <video> 가 메타데이터를 읽을 때 채운다.
  //   아직 모르면 그림 컷처럼 5초로 계산한다(미리보기일 뿐 · 완성 영상은 서버가 파일을 재서 정한다).
  const [clipSeconds, setClipSeconds] = useState<Record<string, number>>({});
  // 완성 영상과 같은 배치(planCutLoop) — 컷 1→2→3→4 순서로 돌며 노래 끝까지 채운다.
  const loopPlan = useMemo(
    () => planCutLoop(
      edit.cuts.map((cut) => ({
        clipSeconds: cut.motion === "ai_video" && cut.clip_url ? clipSeconds[cut.id] ?? null : null,
      })),
      totalDuration,
      // 서버 합성과 같은 겹침 — 크로스페이드는 조각마다 0.5초씩 겹친다(compose COMPOSE_CROSSFADE_SECONDS).
      //    빼먹으면 조각 수가 달라져 미리보기와 결과의 컷 순서가 어긋난다(Codex 검수 2026-10-20).
      edit.edit_settings.transition === "crossfade" ? 0.5 : 0,
    ),
    [edit.cuts, clipSeconds, totalDuration, edit.edit_settings.transition],
  );
  const currentCut = useMemo(() => {
    let elapsed = 0;
    for (const segment of loopPlan) {
      elapsed += segment.seconds;
      if (previewTime < elapsed) return edit.cuts[segment.cutIndex] ?? null;
    }
    return edit.cuts.at(-1) ?? null;
  }, [edit.cuts, loopPlan, previewTime]);
  const composeActive = activeStates.has(compose.state);
  const composeStopping = composeActive && (
    compose.state === "cancel_requested" || (stoppedJobId !== null && compose.job_id === stoppedJobId)
  );
  // 멈추는 동안 막대는 «누른 순간» 값에 멈춘다 — 숨기면 카드 높이가 바뀌어 아래 버튼이 움직인다.
  const progressShown = useRef(0);
  if (!composeStopping) progressShown.current = compose.progress;
  const needsImage = edit.cuts.some((cut) => !cut.image_asset_id);
  const needsClip = edit.cuts.some((cut) => cut.motion === "ai_video" && !cut.clip_asset_id);
  // 지금 AI 영상을 만드는 컷(작품 하나에 하나뿐) — 서버 JOB_ACTIVE 와 같은 범위로 버튼을 잠근다.
  //    모션을 다른 것으로 바꾼 컷은 완료 감시가 사라지므로 잠금에서 뺀다 — 그때는 서버 409 문구가 대신 알린다.
  const videoBusyCutId = edit.cuts.find((cut) => cut.motion === "ai_video"
    && cut.active_video_job
    && activeStates.has(cut.active_video_job.state))?.id
    ?? [...videoStarting][0]
    ?? null;

  useEffect(() => setCompose(edit.compose), [edit.compose]);
  // 막힌 조건이 풀렸으면 그 안내도 지운다 — AI 영상이 다 된 뒤에도 빨간 문구가 남던 자리(2026-10-21 QA).
  useEffect(() => {
    if (composeBlock === "needs_image" && !needsImage) setComposeBlock(null);
    if (composeBlock === "needs_clip" && !needsClip) setComposeBlock(null);
  }, [composeBlock, needsImage, needsClip]);
  // 🩸 만들기가 실패·취소·무효로 끝났으면 다음 「완성하기」는 «새 작업» 이다. 같은 키를 다시 보내면
  //    서버가 끝난 작업을 멱등 응답으로 돌려줘서 화면을 다시 열기 전까지 재시도가 안 됐다(Codex 검수 2026-10-12).
  //    진행 중·성공일 때는 키를 지키고, 요청 자체가 네트워크로 실패한 재시도는 여기 오지 않는다(상태 불변).
  useEffect(() => {
    if (!activeStates.has(compose.state) && compose.state !== "succeeded") composeKey.current = null;
  }, [compose.state]);

  useEffect(() => {
    if (!musicAssetId) return undefined;
    let cancelled = false;
    void apiRequest<{ url: string }>(`/api/assets/${encodeURIComponent(musicAssetId)}/url`)
      .then((result) => {
        if (!cancelled) setAudioUrl(result.url);
      })
      .catch(() => {
        if (!cancelled) setAudioUrl(null);
      });
    return () => { cancelled = true; };
  }, [musicAssetId]);

  useEffect(() => {
    if (!previewing || audioUrl) return undefined;
    const timer = window.setInterval(() => {
      setPreviewTime((current) => {
        if (current + 0.25 >= totalDuration) {
          setPreviewing(false);
          return 0;
        }
        return current + 0.25;
      });
    }, 250);
    return () => window.clearInterval(timer);
  }, [audioUrl, previewing, totalDuration]);

  const moveToFinal = useCallback(async (): Promise<void> => {
    if (finalizing.current) return;
    finalizing.current = true;
    try {
      await apiRequest(`/api/works/${encodeURIComponent(workId)}/autosave`, {
        method: "POST",
        body: JSON.stringify({ step: "final", gifts_v: 2 }),
      });
      navigate(pathForStep(workId, "final"));
    } catch (error) {
      setPageError(editError(error));
      finalizing.current = false;
    }
  }, [workId]);

  useEffect(() => {
    if (activeStates.has(compose.state)) composeWatched.current = true;
  }, [compose.state]);

  useEffect(() => {
    if (compose.state === "succeeded") {
      // 🔑 이 화면에서 만들기가 돌아간 적이 있을 때만 넘긴다 — 위 `composeWatched` 주석 참조.
      if (composeWatched.current) void moveToFinal();
      return undefined;
    }
    if (!activeStates.has(compose.state)) return undefined;
    let cancelled = false;
    let timer: number | null = null;
    const poll = async (): Promise<void> => {
      try {
        const status = await getComposeStatus(workId);
        if (cancelled) return;
        setCompose(status);
        if (status.state === "succeeded") {
          void moveToFinal();
          return;
        }
        if (activeStates.has(status.state)) {
          timer = window.setTimeout(() => void poll(), 1_500);
        }
      } catch (error) {
        if (!cancelled) {
          setPageError(editError(error));
          timer = window.setTimeout(() => void poll(), 3_000);
        }
      }
    };
    void poll();
    return () => {
      cancelled = true;
      if (timer !== null) window.clearTimeout(timer);
    };
  }, [compose.state, moveToFinal, workId]);

  useEffect(() => () => {
    if (settingsTimer.current !== null) window.clearTimeout(settingsTimer.current);
    if (stopUnlockTimer.current !== null) window.clearTimeout(stopUnlockTimer.current);
  }, []);

  const sendSettings = (settings: EditSettings): Promise<void> => {
    pendingSettings.current = null;
    // 🔑 저장은 한 줄로 세운다 — 앞 저장이 가는 중에 새 저장을 따로 보내면 늦게 도착한 앞 저장이
    //    새 설정을 덮거나 합성을 superseded 로 만든다(Codex 재검수 2026-10-21).
    const previous = settingsSave.current ?? Promise.resolve();
    const save = previous.catch(() => undefined).then(() => saveEditSettings(workId, settings)).then((result) => {
      setEdit((current) => current ? { ...current, edit_revision: result.edit_revision } : current);
      if (settingsSave.current === save) setSavingSettings(false);
    }).catch((error: unknown) => {
      // 실패한 설정은 «아직 안 간 것» 으로 되돌린다 — 다음 「완성하기」 가 다시 보낸다.
      // 🩸 뒤에 더 새 저장이 줄 서 있으면 되돌리지 않는다 — 옛 설정이 남아 최신 설정을 덮었다(Codex 재검수).
      if (settingsSave.current === save) pendingSettings.current ??= settings;
      setSavingSettings(false);
      setToast({ message: editError(error), retry: () => updateSettings(settings) });
      throw error;
    }).finally(() => {
      if (settingsSave.current === save) settingsSave.current = null;
    });
    settingsSave.current = save;
    return save;
  };

  const updateSettings = (settings: EditSettings): void => {
    setEdit((current) => current ? { ...current, edit_settings: settings } : current);
    setSavingSettings(true);
    pendingSettings.current = settings;
    if (settingsTimer.current !== null) window.clearTimeout(settingsTimer.current);
    settingsTimer.current = window.setTimeout(() => {
      settingsTimer.current = null;
      void sendSettings(settings).catch(() => undefined);
    }, 500);
  };

  /** 500ms 기다리는 중인 설정은 지금 보내고, 가는 중인 저장은 끝날 때까지 기다린다. */
  const flushSettings = async (): Promise<void> => {
    if (settingsTimer.current !== null) {
      window.clearTimeout(settingsTimer.current);
      settingsTimer.current = null;
    }
    if (pendingSettings.current) await sendSettings(pendingSettings.current);
    if (settingsSave.current) await settingsSave.current;
  };

  const persistOrder = async (cuts: EditableCut[]): Promise<void> => {
    const previous = edit.cuts;
    setEdit((current) => current ? { ...current, cuts } : current);
    try {
      const result = await saveCutOrder(workId, cuts.map((cut) => cut.id));
      setEdit((current) => current ? { ...current, edit_revision: result.edit_revision } : current);
    } catch (error) {
      setEdit((current) => current ? { ...current, cuts: previous } : current);
      setToast({ message: editError(error), retry: () => void persistOrder(cuts) });
    }
  };

  const moveCut = (cutId: string, direction: -1 | 1): void => {
    const index = edit.cuts.findIndex((cut) => cut.id === cutId);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= edit.cuts.length) return;
    const next = [...edit.cuts];
    [next[index], next[target]] = [next[target]!, next[index]!];
    void persistOrder(next);
  };

  const dropCut = (event: DragEvent<HTMLElement>, targetId: string): void => {
    event.preventDefault();
    if (!draggedCutId || draggedCutId === targetId) return;
    const next = [...edit.cuts];
    const from = next.findIndex((cut) => cut.id === draggedCutId);
    const to = next.findIndex((cut) => cut.id === targetId);
    if (from < 0 || to < 0) return;
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved!);
    setDraggedCutId(null);
    void persistOrder(next);
  };

  const setMotion = async (cutId: string, motion: Motion): Promise<void> => {
    const previous = edit.cuts.find((cut) => cut.id === cutId)?.motion;
    setEdit((current) => current ? {
      ...current,
      cuts: current.cuts.map((cut) => cut.id === cutId ? { ...cut, motion } : cut),
    } : current);
    try {
      const result = await saveCutMotion(workId, cutId, motion);
      setEdit((current) => current ? { ...current, edit_revision: result.edit_revision } : current);
    } catch (error) {
      if (previous) {
        setEdit((current) => current ? {
          ...current,
          cuts: current.cuts.map((cut) => cut.id === cutId
            ? { ...cut, motion: previous }
            : cut),
        } : current);
      }
      setToast({ message: editError(error), retry: () => void setMotion(cutId, motion) });
    }
  };

  const makeVideo = async (cutId: string): Promise<void> => {
    if (videoStartingRef.current.has(cutId)) return;
    videoStartingRef.current.add(cutId);
    const key = videoKeys.current.get(cutId) ?? crypto.randomUUID();
    videoKeys.current.set(cutId, key);
    setVideoErrors((current) => ({ ...current, [cutId]: "" }));
    setVideoStarting((current) => new Set(current).add(cutId));
    try {
      const result = await startVideoJob(workId, cutId, key);
      setEdit((current) => current ? {
        ...current,
        cuts: current.cuts.map((cut) => cut.id === cutId
          ? { ...cut, active_video_job: { id: result.job_id, state: "queued" } }
          : cut),
      } : current);
    } catch (error) {
      if (error instanceof ApiError && error.code === "IDEMPOTENCY_CONFLICT") {
        videoKeys.current.delete(cutId);
      }
      setToast({ message: editError(error), retry: () => void makeVideo(cutId) });
    } finally {
      videoStartingRef.current.delete(cutId);
      setVideoStarting((current) => {
        const next = new Set(current);
        next.delete(cutId);
        return next;
      });
    }
  };

  const beginCompose = async (): Promise<void> => {
    setPageError(null);
    setComposeBlock(null);
    if (needsImage) {
      setComposeBlock("needs_image");
      return;
    }
    if (needsClip) {
      setComposeBlock("needs_clip");
      return;
    }
    if (composeStartingRef.current) return;
    composeStartingRef.current = true;
    setComposeStarting(true);
    try {
      await flushSettings();
    } catch {
      composeStartingRef.current = false;
      setComposeStarting(false);
      return;
    }
    const key = composeKey.current ?? crypto.randomUUID();
    composeKey.current = key;
    try {
      const result = await startCompose(workId, key);
      lockStopBriefly();
      setCompose({
        job_id: result.job_id,
        state: "queued",
        queue_position: result.queue_position,
        progress: 0,
      });
    } catch (error) {
      if (error instanceof ApiError && error.code === "IDEMPOTENCY_CONFLICT") {
        composeKey.current = null;
      }
      setToast({ message: editError(error), retry: () => void beginCompose() });
    } finally {
      composeStartingRef.current = false;
      setComposeStarting(false);
    }
  };

  /** 진행 카드가 막 나타난 순간의 «두 번째 탭» 이 중지를 누르지 않게 잠깐 잠근다. */
  const lockStopBriefly = (): void => {
    setStopLocked(true);
    if (stopUnlockTimer.current !== null) window.clearTimeout(stopUnlockTimer.current);
    stopUnlockTimer.current = window.setTimeout(() => {
      stopUnlockTimer.current = null;
      setStopLocked(false);
    }, 1_500);
  };

  const backToCuts = async (): Promise<void> => {
    try {
      await apiRequest(`/api/works/${encodeURIComponent(workId)}/autosave`, {
        method: "POST",
        body: JSON.stringify({ step: "cuts", gifts_v: 2 }),
      });
      navigate(pathForStep(workId, "cuts"));
    } catch (error) {
      setToast({ message: editError(error), retry: () => void backToCuts() });
    }
  };

  const togglePreview = (): void => {
    if (previewing) {
      audioRef.current?.pause();
      setPreviewing(false);
      return;
    }
    if (previewTime >= totalDuration) setPreviewTime(0);
    if (audioRef.current) {
      audioRef.current.currentTime = previewTime;
      void audioRef.current.play().then(() => setPreviewing(true)).catch(() => {
        setPageError("재생 버튼을 다시 눌러 주세요.");
      });
    } else {
      setPreviewing(true);
    }
  };

  return (
    <StudioShell
      currentStep={mvStudioStep.edit}
      imageRegenRemaining={imageRegenRemaining}
      dirty={savingSettings}
      autosaveStatus={savingSettings ? "saving" : "saved"}
    >
      <div className="mv-page edit-page">
        <header className="mv-heading edit-heading">
          <span className="mv-kicker">MV 4 · 편집·완성</span>
          <h1 tabIndex={-1}>컷을 이어 한 편의 영상으로 만들어요</h1>
          <p>순서와 움직임을 고르고, 노래와 함께 미리 확인해요.</p>
        </header>

        <section className="edit-top-grid">
          <div className="edit-preview-panel">
            <div className="edit-preview-stage">
              {currentCut?.image_url
                ? <img src={currentCut.image_url} alt={`미리보기 컷 ${currentCut.idx}: ${currentCut.scene_text}`} />
                : <div className="edit-preview-placeholder">컷 미리보기</div>}
              <span>미리보기 · {formatTime(previewTime)} / {formatTime(totalDuration)} · 720p</span>
            </div>
            <div className="edit-preview-controls">
              <button type="button" aria-label={previewing ? "미리보기 일시정지" : "미리보기 재생"} onClick={togglePreview}>
                {previewing ? "Ⅱ" : "▶"}
              </button>
              <input
                aria-label="미리보기 위치"
                type="range"
                min={0}
                max={Math.max(1, totalDuration)}
                step={0.1}
                value={Math.min(previewTime, totalDuration)}
                onChange={(event) => {
                  const next = Number(event.target.value);
                  setPreviewTime(next);
                  if (audioRef.current) audioRef.current.currentTime = next;
                }}
              />
              <span>{formatTime(previewTime)}</span>
            </div>
            <p className="edit-preview-note">컷 그림을 이어 보는 미리보기라 실제 완성 영상과 조금 달라요.</p>
            {audioUrl ? (
              <audio
                ref={audioRef}
                src={audioUrl}
                preload="metadata"
                onTimeUpdate={(event) => setPreviewTime(event.currentTarget.currentTime)}
                onEnded={() => {
                  setPreviewing(false);
                  setPreviewTime(0);
                }}
                onPause={() => setPreviewing(false)}
              />
            ) : null}
          </div>
          <div className="edit-settings-stack">
            <section className="mv-card edit-setting-card">
              <h2>컷 사이 전환</h2>
              <div className="edit-chip-row">
                {transitions.map((transition) => (
                  <button
                    type="button"
                    className={edit.edit_settings.transition === transition.value ? "selected" : ""}
                    aria-pressed={edit.edit_settings.transition === transition.value}
                    key={transition.value}
                    onClick={() => updateSettings({
                      ...edit.edit_settings,
                      transition: transition.value,
                    })}
                  >{transition.label}</button>
                ))}
              </div>
            </section>
            <section className="mv-card edit-setting-card">
              <h2>제목 카드</h2>
              <div className="edit-chip-row">
                {[true, false].map((enabled) => (
                  <button
                    type="button"
                    className={edit.edit_settings.title_card === enabled ? "selected" : ""}
                    aria-pressed={edit.edit_settings.title_card === enabled}
                    key={String(enabled)}
                    onClick={() => updateSettings({ ...edit.edit_settings, title_card: enabled })}
                  >{enabled ? "켜기" : "끄기"}</button>
                ))}
              </div>
              {/* 🩸 종전 안내는 「노래 제목」이라 했지만 실제로는 줄거리·주제가 나왔다(2026-10-12 조사). */}
              <p>영상 처음에 아래 제목과 내 이름이 잠깐 나와요. 내려받는 파일 이름도 이 제목이에요.</p>
              <TitleEditor
                alwaysOpen
                label="뮤직비디오 제목"
                value={edit.custom_title ?? edit.title}
                // 직접 고친 제목이 있을 때만 «비워서 되돌리기» 를 연다 — 없으면 빈 저장은 할 일이 없다(Codex 재검수 2).
                resetHint={edit.custom_title ? "비워서 저장하면 줄거리로 정해지는 처음 제목으로 돌아가요." : undefined}
                onSave={async (title) => {
                  try {
                    const result = await saveWorkTitle(workId, title);
                    // 비웠으면 자동 제목을 서버가 정한다 — 다시 읽어 맞춘다.
                    const autoTitle = result.custom_title
                      ? null
                      : (await apiRequest<EditResponse>(`/api/works/${encodeURIComponent(workId)}/edit`)).title;
                    setEdit((current) => current ? {
                      ...current,
                      title: result.custom_title ?? autoTitle ?? current.title,
                      custom_title: result.custom_title,
                      edit_revision: result.edit_revision,
                      final_outdated: result.final_outdated ?? current.final_outdated ?? false,
                    } : current);
                    // 리비전이 올랐다 = 다음 「완성하기」 는 새 작업이다.
                    composeKey.current = null;
                  } catch (error) {
                    throw new Error(error instanceof Error && !(error instanceof ApiError) ? error.message : editError(error));
                  }
                }}
              />
              <p>이미 완성 영상을 만들었다면, 다시 만들어야 새 제목이 들어가요.</p>
            </section>
            <section className="mv-card edit-setting-card edit-quality-card">
              <h2>완성 화질</h2>
              <p><b>720p · 16:9</b> — 항상 이 화질로 나와요.</p>
            </section>
          </div>
        </section>

        <section className="edit-timeline-section">
          <header>
            <h2>타임라인</h2>
            <p>끌어서 순서 바꾸기 · ↑↓ 버튼으로도 바꿀 수 있어요 · 컷 1부터 차례로 돌며 노래 끝까지 되풀이해요</p>
          </header>
          <div className="edit-timeline-card">
            <div className="edit-timeline-list">
              {edit.cuts.map((cut, index) => (
                <article
                  className={`edit-timeline-cut${draggedCutId === cut.id ? " dragging" : ""}`}
                  draggable
                  key={cut.id}
                  onDragStart={() => setDraggedCutId(cut.id)}
                  onDragEnd={() => setDraggedCutId(null)}
                  onDragOver={(event) => event.preventDefault()}
                  onDrop={(event) => dropCut(event, cut.id)}
                  style={{ flexGrow: 1 }}
                >
                  <div className="edit-timeline-thumb">
                    {cut.image_url ? <img src={cut.image_url} alt="" /> : null}
                    <span>컷 {index + 1} · {motions.find((motion) => motion.value === cut.motion)?.label}</span>
                  </div>
                  <div className="edit-order-buttons" aria-label={`컷 ${index + 1} 순서 바꾸기`}>
                    <button type="button" disabled={index === 0} aria-label={`컷 ${index + 1} 앞으로`} onClick={() => moveCut(cut.id, -1)}>↑</button>
                    <button type="button" disabled={index === edit.cuts.length - 1} aria-label={`컷 ${index + 1} 뒤로`} onClick={() => moveCut(cut.id, 1)}>↓</button>
                  </div>
                </article>
              ))}
            </div>
            <div className="edit-wave-row" aria-hidden="true">
              <b>♪</b>
              <div>{Array.from({ length: 30 }, (_, index) => <i key={index} style={{ height: `${6 + (index * 7) % 17}px` }} />)}</div>
              <span>{formatTime(totalDuration)}</span>
            </div>
          </div>
        </section>

        <section className="edit-motion-list" aria-label="컷별 움직임">
          {edit.cuts.map((cut, index) => (
            <article className="edit-motion-card" key={cut.id}>
              <div className="edit-motion-preview">
                {cut.clip_url && cut.motion === "ai_video"
                  ? (
                    <video
                      src={cut.clip_url}
                      controls
                      preload="metadata"
                      aria-label={`컷 ${index + 1} AI 영상`}
                      onLoadedMetadata={(event) => {
                        const seconds = event.currentTarget.duration;
                        if (Number.isFinite(seconds) && seconds > 0) {
                          setClipSeconds((current) => current[cut.id] === seconds ? current : { ...current, [cut.id]: seconds });
                        }
                      }}
                    />
                  )
                  : cut.image_url ? <img src={cut.image_url} alt={`컷 ${index + 1}: ${cut.scene_text}`} /> : null}
              </div>
              <div className="edit-motion-content">
                <header>
                  <b>컷 {index + 1}</b>
                  <span>
                    {edit.cuts.length < 2
                      ? `${totalDuration.toFixed(1)}초`
                      : cut.motion === "ai_video" && cut.clip_url
                        ? (clipSeconds[cut.id] ? `한 번에 ${clipSeconds[cut.id]!.toFixed(1)}초` : "한 번에 영상 길이만큼")
                        : `한 번에 ${CUT_LOOP_IMAGE_SECONDS}초`}
                  </span>
                </header>
                <p>{cut.scene_text}</p>
                <div className="edit-motion-chips">
                  {motions.filter((motion) => motion.value !== "ai_video" || edit.video_enabled).map((motion) => (
                    <button
                      type="button"
                      className={cut.motion === motion.value ? "selected" : ""}
                      aria-pressed={cut.motion === motion.value}
                      key={motion.value}
                      onClick={() => void setMotion(cut.id, motion.value)}
                    >
                      <b>{motion.label}</b>
                      <small>{motion.value === "ai_video"
                        ? `남은 ${edit.video_remaining}회 · ${motion.caption}`
                        : motion.caption}</small>
                    </button>
                  ))}
                </div>
                {cut.motion === "ai_video" && edit.video_enabled ? (
                  <VideoCutControls
                    cut={cut}
                    starting={videoStarting.has(cut.id)}
                    otherCutBusy={videoBusyCutId !== null && videoBusyCutId !== cut.id}
                    error={videoErrors[cut.id]}
                    onCreate={() => void makeVideo(cut.id)}
                    onTerminal={(state, errorCode) => {
                      if (state === "failed" || state === "outcome_unknown") {
                        setVideoErrors((current) => ({
                          ...current,
                          [cut.id]: videoFailureMessage(errorCode),
                        }));
                      }
                      videoKeys.current.delete(cut.id);
                      // 새로고침이 실패해도 다른 컷 버튼이 «만드는 중» 잠금에 갇히지 않게 이 컷의 진행 표식을 먼저 지운다.
                      setEdit((current) => current ? {
                        ...current,
                        cuts: current.cuts.map((candidate) => candidate.id === cut.id
                          ? { ...candidate, active_video_job: null }
                          : candidate),
                      } : current);
                      void refresh();
                    }}
                  />
                ) : null}
              </div>
            </article>
          ))}
        </section>

        {/* 🩸 제목만 바꾸면 합성 상태는 여전히 «완성» 이라, 완성작 화면이 옛 제목의 영상을 새 제목으로
             보여 줬다(Codex 검수 2026-10-12). 다시 만들기 전까지 알린다. */}
        {/* 서버가 «지금 완성 영상을 만든 합성» 의 리비전과 비교해 준다 — 다시 들어와도, 다시 만들기를
             취소해도 남는다. 만드는 중에는 진행 표시가 대신한다. */}
        {edit.final_outdated && !composeActive ? (
          <p className="compose-superseded" role="alert">완성 영상을 만든 뒤에 제목이나 편집이 바뀌었어요. 「완성하기」를 다시 눌러야 완성 영상에 반영돼요.</p>
        ) : null}
        {compose.state === "superseded" ? (
          <p className="compose-superseded" role="alert">편집이 바뀌어 다시 만들어야 해요. 컷은 그대로 남아 있어요.</p>
        ) : null}
        {compose.state === "failed" || compose.state === "outcome_unknown" ? (
          <p className="compose-superseded" role="alert">완성 영상을 만들지 못했어요. 컷을 확인하고 다시 시도해 주세요.</p>
        ) : null}
        {composeActive ? (
          <section className="compose-progress-card" aria-live="polite">
            <span className="compose-spinner" aria-hidden="true" />
            <div>
              <b>{composeStopping
                ? "멈추는 중..."
                : compose.state === "queued"
                  ? `완성 영상 차례를 기다리는 중 · 앞에 ${Math.max(0, compose.queue_position - 1)}명`
                  : "완성 영상 만드는 중..."}</b>
              <p>{composeStopping
                ? "잠깐만 기다려 주세요. 만든 컷은 그대로 남아요"
                : "중지하면 편집실로 돌아와요. 만든 컷은 그대로 남아요"}</p>
              {/* 멈추는 동안 막대가 오르면 중지가 안 된 것처럼 보인다 — 누른 순간 값에 고정한다. */}
              <div className="compose-progress-track"><span style={{ width: `${progressShown.current}%` }} /></div>
              {/* 🩸 「중지」 는 카드 왼쪽 아래에 둔다 — 오른쪽 끝에 두면 푸터 「완성하기」 와 거의 같은 자리라,
                   머뭇거리다 다시 누른 학생이 뜻하지 않게 중지를 눌렀다(2026-10-21 QA 실측 (1157,532)→(1180,526)). */}
              <button
                className="button-secondary compose-stop-button"
                type="button"
                // 멈추는 중에는 잠근 채 둔다 — 합성이 끝나 카드가 사라질 때 함께 사라진다.
                disabled={stopLocked || composeStopping}
                onClick={() => {
                  const jobId = compose.job_id;
                  // 중지 요청이 가는 동안 다시 못 누르게 — 두 번 누름이 늦게 도착해 다음 합성을 끊던 자리.
                  setStopLocked(true);
                  setStopping(true);
                  setStoppedJobId(jobId);
                  void stopCompose(workId, jobId).then((result) => {
                    // 🩸 늦게 온 중지 응답(두 번 누름)이 그 사이 새로 시작한 작업을 덮어쓰지 않게 — 같은 작업일 때만.
                    setCompose((current) => current.job_id === jobId
                      ? { job_id: jobId, state: result.state, queue_position: 0, progress: 0 }
                      : current);
                  }).catch((error: unknown) => {
                    // 중지가 안 갔으면 «멈추는 중» 을 풀어 다시 누를 수 있게 한다.
                    setStoppedJobId((current) => current === jobId ? null : current);
                    setToast({ message: editError(error) });
                  }).finally(() => {
                    // 버튼은 «멈추는 중» 이면 composeStopping 으로 잠겨 있다 — 여기서 풀어도 다시 눌리지 않는다.
                    setStopLocked(false);
                    setStopping(false);
                  });
                }}
              >중지</button>
            </div>
          </section>
        ) : null}
        {composeBlock ? (
          <InlineError>{composeBlock === "needs_image"
            ? "모든 컷 그림을 먼저 완성해 주세요."
            : "AI 영상으로 고른 컷을 먼저 만들어 주세요."}</InlineError>
        ) : null}
        {pageError ? <InlineError>{pageError}</InlineError> : null}
        <footer className="mv-footer edit-footer">
          <button className="button-secondary" type="button" disabled={composeActive} onClick={() => void backToCuts()}>이전</button>
          <div>
            <span>{videoBusyCutId !== null && !composeActive
              ? "AI 영상이 끝나면 완성할 수 있어요"
              : "완성한 영상은 지워지지 않아요"}</span>
            {/* 이미 완성작이 있는 채로 편집실에 들어온 경우 — 되돌아갈 길을 화면에 둔다.
                자동 이동을 뗀 자리(위 `composeWatched`)를 사람이 누르는 버튼으로 메운다. */}
            {compose.state === "succeeded" && !composeActive ? (
              <button className="button-secondary" type="button" onClick={() => void moveToFinal()}>완성작 보기</button>
            ) : null}
            {/* 합성 중에도 이 자리에 잠긴 버튼을 둔다 — 같은 자리를 다시 눌러도 아무 일이 없다. */}
            <button
              className="button-primary"
              type="button"
              disabled={composeActive || composeStarting || stopping || videoBusyCutId !== null}
              onClick={() => void beginCompose()}
            >{composeActive ? (composeStopping ? "멈추는 중..." : "만드는 중...") : "완성하기"}</button>
          </div>
        </footer>
      </div>
      {toast ? (
        <div className="edit-toast" role="alert">
          <span>{toast.message}</span>
          {toast.retry ? <button type="button" onClick={toast.retry}>다시 시도</button> : null}
          <button type="button" aria-label="알림 닫기" onClick={() => setToast(null)}>×</button>
        </div>
      ) : null}
    </StudioShell>
  );
}

function VideoCutControls({
  cut,
  starting,
  otherCutBusy,
  error,
  onCreate,
  onTerminal,
}: {
  cut: EditableCut;
  /** 이 컷의 만들기 요청이 가는 중 */
  starting: boolean;
  /** 서버는 작품 하나에 AI 영상 하나만 받는다 — 다른 컷이 만드는 중이면 이 컷 버튼도 잠근다. */
  otherCutBusy: boolean;
  error: string | undefined;
  onCreate: () => void;
  onTerminal: (state: string, errorCode: string | undefined) => void;
}) {
  const jobId = cut.active_video_job?.id ?? null;
  const progress = useJobProgress(jobId);
  const handled = useRef<string | null>(null);
  const state = progress.progress?.state ?? cut.active_video_job?.state;

  useEffect(() => {
    // 🩸 «상태값» 만 기억하면 같은 컷이 두 번 연달아 실패할 때 두 번째 끝을 건너뛰어, 요청 키가 남고
    //    다시 시도가 끝난 작업을 돌려받았다(Codex 검수 2026-10-21). 작업 id 와 함께 기억한다.
    const mark = `${jobId ?? "none"}:${state ?? ""}`;
    // 작업 id 가 비면(끝나서 지운 직후) 훅이 직전 끝 상태를 한 박자 더 들고 있다 — 새 끝으로 세지 않는다.
    if (!jobId || !state || activeStates.has(state) || handled.current === mark) return;
    handled.current = mark;
    onTerminal(state, progress.progress?.error_code);
  }, [jobId, onTerminal, state, progress.progress?.error_code]);

  if (jobId && (!state || activeStates.has(state))) {
    return (
      <div className="video-job-card">
        <span className="compose-spinner" aria-hidden="true" />
        <div><b>{state === "queued" ? "AI 영상 차례 기다리는 중" : "AI 영상 만드는 중"}</b><small>다 되면 알려드려요.</small></div>
        <button
          className="button-secondary"
          type="button"
          onClick={() => void apiRequest(`/api/jobs/${encodeURIComponent(jobId)}/cancel`, {
            method: "POST",
            body: JSON.stringify({}),
          })}
        >중지</button>
      </div>
    );
  }
  if (error) {
    return (
      <div className="video-failure-card" role="alert">
        <b>{error}</b>
        <span>몰래 다른 방식으로 만들지 않고 알려드려요.</span>
        <button className="button-secondary" type="button" disabled={starting || otherCutBusy} onClick={onCreate}>다시 시도</button>
      </div>
    );
  }
  if (cut.clip_asset_id) return <p className="video-ready-label">AI 영상 준비 완료 ✓</p>;
  return (
    <>
      <button
        className="button-primary make-video-button"
        type="button"
        disabled={starting || otherCutBusy}
        onClick={onCreate}
      >{starting ? "요청하는 중..." : "이 컷 AI 영상 만들기"}</button>
      {otherCutBusy ? <small className="video-wait-note">다른 컷의 AI 영상이 끝나면 만들 수 있어요.</small> : null}
    </>
  );
}
